import { leagueFetch } from '@leagueplatform/league-fetch';
import { captureAppError } from '@leagueplatform/observability';
import { SENTRY_CONSTANTS } from 'common/constants';

export interface PartnerTokenResponse {
  data: {
    id: string;
    type: 'identity-partner-token';
    attributes: {
      accessToken: string;
      idToken: string;
      partnerId: string;
    };
  };
}

export interface PartnerTokenRequest {
  partnerId: string;
}

/**
 * Fetch partner identity token for the specified partner ID.
 * @param partnerId - The partner ID to get tokens for.
 * @returns The partner token response.
 * @throws Error if the request fails.
 */
export const getPartnerIdentityToken = async (
  partnerId: string,
): Promise<PartnerTokenResponse> => {
  const response = await leagueFetch(
    `/v1/identity-partner-token?partnerId=${encodeURIComponent(partnerId)}`,
    {
      method: 'GET',
      errorContext: {
        ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
      },
    },
  );

  if (!response.ok) {
    const errorMessage = `Failed to fetch partner token for partnerId: ${partnerId}. Status: ${response.status}`;
    captureAppError(new Error(errorMessage), {
      ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
      context: { contextDetails: { partnerId, status: response.status } },
    });
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return data as PartnerTokenResponse;
};
